<template>
  <span :class="badgeClasses">
    <slot />
  </span>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  variant: {
    type: String,
    default: 'neutral',
    validator: (value) => ['success', 'danger', 'info', 'warning', 'neutral', 'primary'].includes(value)
  },
  size: {
    type: String,
    default: 'md',
    validator: (value) => ['sm', 'md', 'lg'].includes(value)
  },
  outlined: {
    type: Boolean,
    default: false
  },
  rounded: {
    type: Boolean,
    default: false
  },
  dot: {
    type: Boolean,
    default: false
  }
})

const variantClasses = computed(() => {
  if (props.outlined) {
    const outlined = {
      success: 'bg-transparent border border-green-600 text-green-700 dark:text-green-400',
      danger: 'bg-transparent border border-red-600 text-red-700 dark:text-red-400',
      info: 'bg-transparent border border-blue-600 text-blue-700 dark:text-blue-400',
      warning: 'bg-transparent border border-yellow-600 text-yellow-700 dark:text-yellow-400',
      neutral: 'bg-transparent border border-gray-600 text-gray-700 dark:text-gray-300',
      primary: 'bg-transparent border border-violet-600 text-violet-700 dark:text-violet-400'
    }
    return outlined[props.variant]
  }

  const filled = {
    success: 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200',
    danger: 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200',
    info: 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200',
    warning: 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200',
    neutral: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200',
    primary: 'bg-violet-100 dark:bg-violet-900 text-violet-800 dark:text-violet-200'
  }
  return filled[props.variant]
})

const sizeClasses = computed(() => {
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-2.5 py-1 text-xs',
    lg: 'px-3 py-1.5 text-sm'
  }
  return sizes[props.size]
})

const shapeClasses = computed(() => {
  return props.rounded ? 'rounded-full' : 'rounded'
})

const dotClasses = computed(() => {
  if (!props.dot) return ''

  const dotColors = {
    success: 'before:bg-green-500',
    danger: 'before:bg-red-500',
    info: 'before:bg-blue-500',
    warning: 'before:bg-yellow-500',
    neutral: 'before:bg-gray-500',
    primary: 'before:bg-violet-500'
  }

  return `flex items-center gap-1.5 before:content-[''] before:w-1.5 before:h-1.5 before:rounded-full ${dotColors[props.variant]}`
})

const badgeClasses = computed(() => {
  return [
    'inline-flex items-center font-medium',
    variantClasses.value,
    sizeClasses.value,
    shapeClasses.value,
    dotClasses.value
  ].filter(Boolean).join(' ')
})
</script>
